import React, { useState, useEffect } from 'react';
import { api } from '../api';

/**
 * Stripe Express Account Setup Component
 * Allows HOAs to set up payment processing
 */
const StripeExpressSetup = ({ hoaId }) => {
  const [accountStatus, setAccountStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (hoaId) {
      loadAccountStatus();
    }
  }, [hoaId]);

  const loadAccountStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/stripe/express/status');
      setAccountStatus(response.data);
    } catch (error) {
      console.error('Failed to load Express account status:', error);
      // Check for Connect platform error
      const errorMsg = error.response?.data?.detail || error.message || '';
      if (errorMsg.includes('Stripe Connect') || errorMsg.includes('Only Stripe Connect platforms')) {
        setAccountStatus({
          error: 'connect_not_enabled',
          message: 'Stripe Connect is not enabled on this account. Platform admin needs to enable it in Stripe Dashboard.'
        });
      } else {
        setAccountStatus({
          error: 'unknown',
          message: errorMsg || 'Failed to load account status'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAccount = async () => {
    try {
      setProcessing(true);
      const response = await api.post('/api/stripe/express/create-account');
      
      if (response.data.success) {
        alert('Express account created! Now complete onboarding.');
        await loadAccountStatus();
        // Automatically open onboarding
        handleStartOnboarding();
      }
    } catch (error) {
      console.error('Failed to create Express account:', error);
      const errorMsg = error.response?.data?.detail || error.message;
      
      // Check for platform profile error
      if (errorMsg.includes('platform-profile') || errorMsg.includes('Platform profile')) {
        setAccountStatus({
          error: 'platform_profile_incomplete',
          message: errorMsg
        });
      } else {
        alert(`❌ Failed to create account: ${errorMsg}`);
      }
    } finally {
      setProcessing(false);
    }
  };

  const handleStartOnboarding = async () => {
    try {
      setProcessing(true);
      const response = await api.post('/api/stripe/express/onboard-link');
      
      if (response.data.success) {
        // Open Stripe onboarding in new window
        window.open(response.data.onboarding_url, '_blank');
      }
    } catch (error) {
      console.error('Failed to create onboarding link:', error);
      alert(error.response?.data?.detail || 'Failed to create onboarding link');
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenDashboard = async () => {
    console.log('🔵 Button clicked - handleOpenDashboard fired!');
    
    try {
      setProcessing(true);
      console.log('🔵 Making API call to /api/stripe/express/dashboard-link');
      const response = await api.post('/api/stripe/express/dashboard-link');
      
      console.log('🔵 Dashboard response:', response.data);
      
      if (response.data.success && response.data.dashboard_url) {
        console.log('🔵 Opening URL:', response.data.dashboard_url);
        
        try {
          const opened = window.open(response.data.dashboard_url, '_blank', 'noopener,noreferrer');
          
          if (!opened || opened.closed || typeof opened.closed === 'undefined') {
            // Popup was blocked
            alert('⚠️ Popup blocked! Please allow popups for this site and try again.\n\nDashboard URL: ' + response.data.dashboard_url);
          }
        } catch (popupError) {
          console.error('❌ Popup error:', popupError);
          alert('⚠️ Unable to open popup. Please check your browser settings.\n\nDashboard URL: ' + response.data.dashboard_url);
        }
      } else {
        console.error('❌ No dashboard URL in response:', response.data);
        alert('Unable to open dashboard. No URL received. Please try again.');
      }
    } catch (error) {
      console.error('❌ Failed to create dashboard link:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to open dashboard';
      alert('❌ Error opening Stripe dashboard:\n\n' + errorMsg);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    );
  }

  const getStatusBadge = () => {
    if (!accountStatus) return null;
    
    const status = accountStatus.status;
    
    if (status === 'not_created') {
      return <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">Not Set Up</span>;
    } else if (status === 'active' && accountStatus.charges_enabled) {
      return <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">✅ Active</span>;
    } else if (status === 'pending') {
      return <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm">⏳ Pending</span>;
    }
    
    return <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">{status}</span>;
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Payment Processing Setup</h3>
          <p className="text-sm text-gray-600 mt-1">
            Set up Stripe to accept payments from homeowners
          </p>
        </div>
        {getStatusBadge()}
      </div>

      {/* Error: Connect Not Enabled */}
      {accountStatus?.error === 'connect_not_enabled' && (
        <div className="space-y-4">
          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
            <h4 className="font-bold text-red-900 mb-3 flex items-center gap-2">
              <span>⚠️</span>
              <span>Stripe Connect Not Enabled</span>
            </h4>
            <div className="text-sm text-red-800 space-y-3">
              <p className="font-medium">
                The platform's Stripe account is not configured for Connect.
              </p>
              <p>
                <strong>What this means:</strong> HOAs cannot set up payment processing until the platform administrator enables Stripe Connect.
              </p>
              <div className="bg-white border border-red-200 rounded p-3 mt-3">
                <p className="font-semibold text-red-900 mb-2">Platform Admin Action Required:</p>
                <ol className="list-decimal list-inside space-y-1 text-red-800">
                  <li>Go to Stripe Dashboard → <a href="https://dashboard.stripe.com/settings/connect" target="_blank" rel="noopener noreferrer" className="underline font-medium">Connect Settings</a></li>
                  <li>Enable Stripe Connect for Express accounts</li>
                  <li>Enable <strong>"Card payments"</strong> and <strong>"Transfers"</strong> capabilities</li>
                  <li>Save settings and wait for approval (usually instant)</li>
                  <li>Refresh this page to set up payment processing</li>
                </ol>
              </div>
              <div className="bg-blue-50 border border-blue-300 rounded p-3 mt-3">
                <p className="font-semibold text-blue-900 mb-1">ℹ️ What is Stripe Connect?</p>
                <p className="text-blue-800 text-sm">
                  Stripe Connect allows HOAs to have their own payment accounts. Each HOA receives funds directly to their bank account while you manage the platform.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error: Platform Profile Incomplete */}
      {accountStatus?.error === 'platform_profile_incomplete' && (
        <div className="space-y-4">
          <div className="bg-red-50 border-2 border-red-300 rounded-lg p-6">
            <h4 className="font-bold text-red-900 mb-3 flex items-center gap-2">
              <span>⚠️</span>
              <span>Platform Profile Incomplete</span>
            </h4>
            <div className="text-sm text-red-800 space-y-3">
              <p className="font-medium">
                Your Stripe platform profile needs to be completed before creating Express accounts.
              </p>
              <p>
                <strong>What this means:</strong> Stripe requires platform operators to complete their business profile before allowing connected accounts.
              </p>
              <div className="bg-white border border-red-200 rounded p-3 mt-3">
                <p className="font-semibold text-red-900 mb-2">Platform Admin Action Required:</p>
                <ol className="list-decimal list-inside space-y-1 text-red-800">
                  <li>Go to Stripe Dashboard → <a href="https://dashboard.stripe.com/settings/public-details" target="_blank" rel="noopener noreferrer" className="underline font-medium">Business Settings</a></li>
                  <li>Complete all required business information</li>
                  <li>Verify your business details and bank account</li>
                  <li>Submit for Stripe review (usually takes 1-2 business days)</li>
                  <li>Once approved, return here to set up payment processing</li>
                </ol>
              </div>
              <div className="bg-blue-50 border border-blue-300 rounded p-3 mt-3">
                <p className="font-semibold text-blue-900 mb-1">💡 Tip</p>
                <p className="text-blue-800 text-sm">
                  This is a one-time setup for the platform. Once completed, all HOAs can create their Express accounts immediately.
                </p>
              </div>
              {accountStatus?.message && (
                <div className="bg-gray-50 border border-gray-300 rounded p-3 mt-3">
                  <p className="font-semibold text-gray-900 mb-1">Error Details:</p>
                  <p className="text-gray-700 text-sm font-mono">{accountStatus.message}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {accountStatus?.status === 'not_created' && !accountStatus?.error && (
        <div className="space-y-4">
          {/* Stripe Business Classification Info */}
          <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
            <h4 className="font-bold text-green-900 mb-2 flex items-center gap-2">
              <span>📋</span>
              <span>HOA Business Classification for Stripe</span>
            </h4>
            <div className="text-sm text-green-800 space-y-2">
              <div>
                <strong>Industry:</strong> Homeowners' Association / Community Association
              </div>
              <div>
                <strong>MCC Code:</strong> 8398 — Charitable and Social Service Organizations (Non-Profit)
              </div>
              <div>
                <strong>Description:</strong> "Non-profit homeowners' association collecting assessments, dues and payments for community amenities and services."
              </div>
              <div>
                <strong>Products/Services:</strong> "Association membership dues, annual assessments, amenity fees, community event fees."
              </div>
              <div className="mt-2 p-2 bg-yellow-100 border border-yellow-300 rounded">
                <strong>⚠️ Important Note:</strong> Tell Stripe: "Please classify this account under non-profit/social services as it is an HOA not a retail business."
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">📝 What You'll Need:</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• HOA's legal business name & Tax ID (EIN)</li>
              <li>• Bank account details for payouts</li>
              <li>• Representative's personal information (SSN/ITIN)</li>
              <li>• Business address and contact information</li>
              <li>• Website URL (use your Public Page from Settings)</li>
            </ul>
          </div>

          <button
            onClick={handleCreateAccount}
            disabled={processing}
            className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
          >
            {processing ? 'Creating Account...' : '🚀 Set Up Payment Processing'}
          </button>

          {/* Link to Full Guide */}
          <div className="text-center">
            <a
              href="/STRIPE_SETUP_GUIDE.md"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              📖 View Complete Setup Guide
            </a>
          </div>
        </div>
      )}

      {accountStatus?.status === 'pending' && (
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h4 className="font-medium text-yellow-900 mb-2">⏳ Complete Onboarding</h4>
            <p className="text-sm text-yellow-800">
              Your Express account is created but onboarding is not complete.
              Click below to continue setup.
            </p>
          </div>

          <button
            onClick={handleStartOnboarding}
            disabled={processing}
            className="w-full px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 font-medium"
          >
            {processing ? 'Opening...' : '📝 Complete Onboarding'}
          </button>
        </div>
      )}

      {accountStatus?.status === 'active' && accountStatus.charges_enabled && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-medium text-green-900 mb-2">✅ Payment Processing Active</h4>
            <p className="text-sm text-green-800 mb-3">
              Your HOA can now accept payments from homeowners.
            </p>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-green-700 font-medium">Charges:</span>
                <span className="ml-2 text-green-600">
                  {accountStatus.charges_enabled ? '✓ Enabled' : '✗ Disabled'}
                </span>
              </div>
              <div>
                <span className="text-green-700 font-medium">Payouts:</span>
                <span className="ml-2 text-green-600">
                  {accountStatus.payouts_enabled ? '✓ Enabled' : '✗ Disabled'}
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleOpenDashboard}
              disabled={processing}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              {processing ? 'Opening...' : '📊 View Dashboard'}
            </button>
            <button
              onClick={loadAccountStatus}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              🔄 Refresh Status
            </button>
          </div>
        </div>
      )}

      {/* General Errors */}
      {accountStatus?.error === 'unknown' && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <h4 className="font-medium text-orange-900 mb-2">⚠️ Setup Error</h4>
          <p className="text-sm text-orange-800 mb-3">
            {accountStatus.message}
          </p>
          <button
            onClick={loadAccountStatus}
            className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 text-sm font-medium"
          >
            🔄 Retry
          </button>
        </div>
      )}

      {accountStatus?.requirements && accountStatus.requirements.currently_due?.length > 0 && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <h4 className="font-medium text-red-900 mb-2">⚠️ Action Required</h4>
          <p className="text-sm text-red-800">
            Stripe needs additional information to activate your account.
          </p>
          <button
            onClick={handleStartOnboarding}
            className="mt-2 text-sm text-red-700 underline hover:text-red-900"
          >
            Complete requirements →
          </button>
        </div>
      )}

      {/* Help Section for Bank Account Setup */}
      {accountStatus?.charges_enabled && (
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 mb-2">💳 Payment Methods Setup</h4>
          <div className="text-sm text-blue-800 space-y-2">
            <p>
              <strong>To enable bank account payments (ACH):</strong>
            </p>
            <ol className="list-decimal ml-5 space-y-1">
              <li>Click "View Dashboard" above to open your Stripe Express dashboard</li>
              <li>Navigate to <strong>Settings → Payment methods</strong></li>
              <li>Enable <strong>ACH Direct Debit</strong> (US bank account payments)</li>
              <li>Also available: ACH Credit Transfer, Wire Transfer</li>
            </ol>
            <p className="mt-3">
              <strong>Credit/Debit Cards:</strong> Already enabled by default
            </p>
            <p className="text-xs text-blue-600 mt-2">
              💡 ACH payments typically have lower fees (0.8%, capped at $5) compared to cards (2.9% + 30¢)
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StripeExpressSetup;
